import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import QRCode from 'qrcode';
import kioskService from '../../services/api/kiosk.service';

/**
 * Ecranul de blocare al unui terminal public.
 *
 * Opac și pe tot ecranul, intenționat: acoperirea muncii persoanei precedente e
 * tot rostul. Arată un QR pe care omul îl scanează cu camera telefonului, plus
 * numele terminalului și ultimele 4 caractere ale codului, ca să poată verifica
 * dacă ce apare pe telefon corespunde mașinii din fața lui — asta e apărarea
 * împotriva unui QR afișat pe alt ecran.
 *
 * APARE ÎN FIECARE TAB în care e deschis stocul, dar **doar un tab generează
 * codul**. Distincția nu e cosmetică: pe server un cod nou îl retrage pe cel
 * precedent al terminalului, deci două taburi care generează independent se
 * invalidează reciproc într-o buclă strânsă. Pe primul terminal real, o
 * duzină de taburi au produs ~3000 de coduri pe minut și nimeni nu putea scana
 * nimic, fiindcă QR-ul murea înainte ca telefonul să apuce să focalizeze.
 *
 * Alegerea tabului care generează se face cu Web Locks: cine ține lacătul
 * `kiosk-qr-minter` generează și difuzează codul, ceilalți îl afișează. Dacă
 * tabul acela e închis, lacătul se eliberează și îl preia altul automat.
 */

const PAIR_BASE_URL = import.meta.env.VITE_KIOSK_PAIR_BASE_URL || window.location.origin;
const ROTATE_MS = 60000;
// Pauză după un cod respins/expirat sau după un 429. Fără ea, un client care
// reacționează instant la „expirat" poate inunda serverul.
const RETRY_MS = 5000;

export default function KioskLockOverlay({ terminal, resolving, onPaired }) {
  const [pairing, setPairing] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [error, setError] = useState(null);
  const [isMinter, setIsMinter] = useState(false);
  const generationRef = useRef(0);
  const onPairedRef = useRef(onPaired);
  onPairedRef.current = onPaired;

  useEffect(() => {
    if (resolving || !terminal) return;

    let cancelled = false;
    const generation = ++generationRef.current;
    const alive = () => !cancelled && generationRef.current === generation;

    const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('kiosk') : null;
    let current = null;
    let expiryTimer;
    let qrVersion = 0;

    function clearQr(message = null) {
      clearTimeout(expiryTimer);
      ++qrVersion;
      setPairing(null);
      setQrDataUrl(null);
      setError(message);
    }

    async function showQr(pair) {
      clearQr();
      const version = qrVersion;
      const remaining = pair.expiresAt - Date.now();
      if (remaining <= 0) return;
      setPairing(pair);
      expiryTimer = setTimeout(() => {
        if (alive()) clearQr('Codul a expirat. Se generează automat unul nou.');
      }, remaining);
      const dataUrl = await QRCode.toDataURL(`${PAIR_BASE_URL}/p/${pair.code}`, {
        width: 560,
        margin: 1,
        errorCorrectionLevel: 'M',
      });
      if (alive() && version === qrVersion && Date.now() < pair.expiresAt) setQrDataUrl(dataUrl);
    }

    if (channel) {
      channel.addEventListener('message', (event) => {
        const message = event.data;
        if (!message?.type || !alive()) return;
        if (message.type === 'qr' && !current) {
          // Tab care doar afișează: preia codul generat de tabul cu lacătul.
          showQr(message.pairing).catch(() => {});
        } else if (message.type === 'qr-unavailable' && !current) {
          clearQr(message.error);
        } else if (message.type === 'qr-request' && current) {
          // Un tab nou s-a deschis și are nevoie de codul curent imediat, ca să
          // nu stea până la următoarea rotație cu ecranul gol.
          channel.postMessage({ type: 'qr', pairing: current });
        }
      });
      channel.postMessage({ type: 'qr-request' });
    }

    async function mintLoop() {
      setIsMinter(true);
      let rotateAt = 0;

      while (alive()) {
        try {
          if (!current || Date.now() >= rotateAt) {
            // Înainte de a genera un cod nou, verific dacă s-a deschis o sesiune
            // fără ca poll-ul acestui tab să o vadă — un tab frate a împerecheat
            // și mesajul cross-tab s-a pierdut.
            if (await onPairedRef.current()) return;
            if (!alive()) return;
            const requestedAt = Date.now();
            const started = await kioskService.pairStart();
            if (!alive()) return;
            current = {
              code: started.code,
              code_suffix: started.code_suffix,
              expiresAt: requestedAt + started.expires_in * 1000,
            };
            await showQr(current);
            channel?.postMessage({ type: 'qr', pairing: current });
            rotateAt = Date.now() + ROTATE_MS;
          }

          const result = await kioskService.pairPoll(current.code);
          if (!alive()) return;

          if (result.status === 'approved') {
            if (await onPairedRef.current()) return;
            current = null;
            clearQr();
            channel?.postMessage({ type: 'qr-unavailable', error: null });
            await new Promise((resolve) => setTimeout(resolve, RETRY_MS));
          } else if (result.status === 'expired') {
            current = null;
            clearQr();
            channel?.postMessage({ type: 'qr-unavailable', error: null });
            await new Promise((resolve) => setTimeout(resolve, RETRY_MS));
          }
        } catch (err) {
          // A failed long-poll must not end the loop and leave a stale QR on
          // screen. Keep the minter lock and retry after the network recovers.
          if (!alive()) return;
          current = null;
          const message = err.status === 429 ? null : 'Conexiunea a fost întreruptă. Reîncercăm automat…';
          clearQr(message);
          channel?.postMessage({ type: 'qr-unavailable', error: message });
          await new Promise((resolve) => setTimeout(resolve, RETRY_MS));
        }
      }
    }

    // Web Locks: exact un tab per profil de browser ajunge aici. Ceilalți
    // așteaptă în coadă și preiau dacă tabul acela se închide.
    if (navigator.locks?.request) {
      navigator.locks
        .request('kiosk-qr-minter', async () => {
          if (alive()) await mintLoop();
        })
        .catch(() => {});
    } else {
      // Browser fără Web Locks: generează, cu riscul asumat de coliziune —
      // bariera de pe server ține rata sub control oricum.
      mintLoop().catch(() => {});
    }

    return () => {
      cancelled = true;
      clearTimeout(expiryTimer);
      channel?.close();
    };
  }, [resolving, terminal]);

  const terminalName = terminal?.name || kioskService.getKioskTerminalName() || 'Terminal';

  return (
    <div
      // Verde de brand cu text închis. Alb pe brand-primary e doar 2,68:1 —
      // sub minimul WCAG AA — iar ecranul ăsta se citește de la câțiva metri
      // într-o hală, deci textul e brand-secondary-dark (5,84:1). Textul nu se
      // poate selecta: pe un terminal, un titlu rămas evidențiat e zgomot
      // vizual pe care nimeni nu-l poate anula.
      className="fixed inset-0 flex select-none flex-col items-center justify-center bg-brand-primary text-brand-secondary-dark"
      // Peste orice dialog, drawer sau toast din aplicație (toast-urile sunt la 100000).
      style={{ zIndex: 2147483000 }}
      role="dialog"
      aria-modal="true"
      aria-label="Terminal blocat"
    >
      <div className="mb-8 text-center">
        <p className="text-sm font-semibold uppercase tracking-widest text-brand-secondary-dark/70">
          Terminal blocat
        </p>
        <h1 className="mt-2 text-3xl font-bold">{terminalName}</h1>
      </div>

      {resolving && <p className="text-lg font-medium">Se verifică sesiunea…</p>}

      {!resolving && error && (
        // Un panou roșu pe fond verde arată ca o pagină stricată; un card alb cu
        // text roșu închis păstrează eroarea evidentă fără să lupte cu brandul.
        <div className="max-w-md rounded-xl bg-white px-6 py-5 text-center shadow-lg">
          <p className="text-lg font-semibold text-brand-error-dark">{error}</p>
          <p className="mt-2 text-sm text-brand-secondary">
            Așteaptă apariția noului cod QR, apoi scanează-l cu telefonul.
          </p>
        </div>
      )}

      {!resolving && !error && (
        <>
          {/* QR-ul stă în card alb: scanerul are nevoie de tiparul negru-pe-alb
              și de zona liniștită, deci culoarea de brand nu trebuie să-l atingă. */}
          <div className="rounded-2xl bg-white p-5 shadow-2xl">
            {qrDataUrl ? (
              <img src={qrDataUrl} alt="Cod QR de autentificare" className="h-72 w-72" />
            ) : (
              <div className="flex h-72 w-72 items-center justify-center text-brand-secondary/60">
                Se generează…
              </div>
            )}
          </div>

          <p className="mt-8 max-w-md text-center text-lg font-medium leading-relaxed">
            Deschide camera telefonului, scanează codul și apasă
            {' '}<span className="font-bold">Autorizează</span>.
          </p>

          {pairing?.code_suffix && (
            // Chip închis pe fondul verde: 5,84:1 pentru chip, iar în interior
            // alb pe brand-secondary-dark, deci cele patru caractere pe care
            // omul trebuie efectiv să le compare au cel mai bun contrast.
            <div className="mt-6 flex items-center gap-3 rounded-full bg-brand-secondary-dark px-6 py-3">
              <span className="text-sm uppercase tracking-widest text-white/70">Cod</span>
              <span className="font-mono text-2xl tracking-[0.3em] text-white">
                {pairing.code_suffix}
              </span>
            </div>
          )}

          <p className="mt-4 text-sm text-brand-secondary-dark/80">
            Verifică pe telefon că apar același nume și același cod.
            {!isMinter && pairing ? ' (cod generat de alt tab)' : ''}
          </p>
        </>
      )}
    </div>
  );
}

KioskLockOverlay.propTypes = {
  terminal: PropTypes.shape({
    name: PropTypes.string,
    idle_timeout_seconds: PropTypes.number,
  }),
  resolving: PropTypes.bool,
  onPaired: PropTypes.func.isRequired,
};
